import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { Draggable } from 'react-beautiful-dnd';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { toUserTimezone } from 'utils';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/EditOutlined';
import NotificationsIcon from '@material-ui/icons/NotificationsActiveOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import ChatIcon from '@material-ui/icons/MessageOutlined';
import { useConfirm } from 'material-ui-confirm';
import { COLORS } from './constants';
import NoteModal from './components/NoteModal';

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.05)',
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderLeft: '5px solid',
    borderColor: (props) => props.color || theme.palette.text.secondary,
  },
  name: {
    fontWeight: 300,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
}));

const TodoCard = ({
  data,
  user,
  onEdit,
  onDelete,
  sendNotification,
  provided
}) => {
  const classes = useStyles({ color: COLORS[data.priority] });
  const confirm = useConfirm();
  const [showModal, setShowModal] = useState(false);

  const handleDelete = () =>
    confirm({
      title: 'Delete Task?',
      description: 'This task will be deleted permanently.',
      confirmationText: 'Delete',
    }).then(() => onDelete(data));

  return (
      <Box
          className={classes.card}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
      >
        <Box display="flex" alignItems="flex-start">
          <Typography gutterBottom variant="body2" color="textPrimary">
            {data?.title}
          </Typography>
          <Box marginLeft="auto" display="flex" alignItems="center">
            {user.type !== 'admin' && (
                <IconButton size="small" onClick={() => setShowModal(true)}>
                  <ChatIcon fontSize="small" />
                </IconButton>
            )}
            {user.type === 'admin' && (
                <>
                  <IconButton size="small" onClick={() => onEdit(data)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                      size="small"
                      onClick={() => sendNotification(data)}
                  >
                    <NotificationsIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                      size="small"
                      color="secondary"
                      onClick={handleDelete}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
            )}
          </Box>
        </Box>
        {data?.note && <Typography gutterBottom>{data.note}</Typography>}
        <Box>
          <Typography variant="caption" color="textSecondary">
            {user.type === 'admin' ? 'Assigned To' : 'Task Director'}
          </Typography>
          {user.type === 'admin' ? (
              <Typography gutterBottom variant="subtitle2" color="textPrimary">
                {`${data?.user?.user_first_name} ${data?.user?.user_last_name}`}
              </Typography>
          ) : (
              <Typography gutterBottom variant="subtitle2" color="textPrimary">
                {`${data?.assignedBy?.user_first_name} ${data?.assignedBy?.user_last_name}`}
              </Typography>
          )}
          {user.type !== 'student' && data?.student && (
              <>
                <Typography variant="caption" color="textSecondary">
                  Student
                </Typography>
                <Typography
                    gutterBottom
                    variant="subtitle2"
                    color="textPrimary"
                >
                  {`${data?.student?.name || ''}`}
                </Typography>
              </>
          )}
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary">
            Due Date
          </Typography>
          <Typography gutterBottom variant="subtitle2" color="textPrimary">
            {data?.due_date
                ? toUserTimezone(data?.due_date, user.timezone).format(
                    'MMM DD, YYYY',
                )
                : 'NA'}
          </Typography>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Reminder
            </Typography>
            <Typography gutterBottom variant="subtitle2" color="textPrimary">
              {data?.due_date
                  ? toUserTimezone(data?.reminder, user.timezone).format(
                      'MMM DD, YYYY HH:mm',
                  )
                  : 'NA'}
            </Typography>
          </Box>
        </Box>
        {showModal && (
            <NoteModal onClose={() => setShowModal(false)} task={data} />
        )}
      </Box>
  );
};

export default TodoCard;
